import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Button,
  Checkbox,
  FormControlLabel,
  Switch,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  Edit,
  Clear,
  Download,
  ArrowBack,
  Warning,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { Application } from './admin/types/application';
import SignatureCanvas from 'react-signature-canvas';
import { generateContractPDF, downloadPDF } from '../utils/contractPdfGenerator';
import { isContractFeatureAvailable, getProcessMessage, isSmartphone, isTablet, isWearable } from '../utils/deviceCategoryUtils';

// 📋 계약 조항 상수 정의
const CONTRACT_TERMS = [
  {
    id: 'ownership',
    title: '소유권 확인',
    content: '판매하는 기기는 본인 소유이며, 분실/도난 신고된 기기가 아닙니다.',
    required: true,
  },
  {
    id: 'lost_stolen_responsibility',
    title: '분실/도난 책임',
    content: '분실/도난 기기가 아님을 확인 후 매도 하였으며, 추후에 분실/도난 사유가 발생할 경우 모든 민·형사 책임을 지는 것에 동의 합니다.',
    required: true,
  },
  {
    id: 'unpaid_responsibility',
    title: '미납/연체 책임',
    content: '미납/연체/직권해지 등 사유로 추후에 타인이 이용시 문제가 발생할 경우 모든 민·형사 책임을 지는 것에 동의 합니다.',
    required: true,
  },
  {
    id: 'no_refund_agreement',
    title: '거래 취소 불가',
    content: '매도 후 단순 변심 및 기타 사유로 거래 취소 및 환불이 될 수 없음에 동의 합니다.',
    required: true,
  },
  {
    id: 'final_sale',
    title: '최종 판매 확정',
    content: '계약 체결 후 판매 취소나 기기 반환을 요구하지 않습니다.',
    required: true,
  },
  {
    id: 'payment_terms',
    title: '입금 조건',
    content: '영업일 기준 2-3일 내 지정 계좌로 입금받는 것에 동의합니다.',
    required: false,
  },
];

interface ContractPageProps {}

export default function ContractPage({}: ContractPageProps) {
  const { application_id } = useParams<{ application_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 📊 상태 관리
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<Application | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [contractStatus, setContractStatus] = useState<'draft' | 'completed' | 'error'>('draft');
  
  // ✅ 체크박스 상태
  const [checkedTerms, setCheckedTerms] = useState<Record<string, boolean>>({});
  const [allTermsChecked, setAllTermsChecked] = useState(false);
  const [showDetailedTerms, setShowDetailedTerms] = useState(true); // 개별 조항 표시 여부
  
  // ✍️ 서명 관련 상태
  const signatureRef = useRef<SignatureCanvas>(null);
  const [signatureCompleted, setSignatureCompleted] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  
  // 🔄 제출 관련 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showExitDialog, setShowExitDialog] = useState(false);


  // 🔄 신청서 데이터 로드
  useEffect(() => {
    const loadApplication = async () => {
      if (!application_id) {
        setError('잘못된 접근입니다.');
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'applications', application_id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          setError('신청서를 찾을 수 없습니다.');
          setLoading(false);
          return;
        }

        const appData = { id: docSnap.id, ...docSnap.data() } as Application;
        const params0 = new URLSearchParams(location.search);
        const fromQueryDeviceId0 = params0.get('device_id');

        // 🚫 계약서 기능 사용 불가(웨어러블 등)인 경우
        if (!isContractFeatureAvailable(appData.devices || [])) {
          setError('계약서 기능은 스마트폰/태블릿 전용입니다. ' + getProcessMessage(appData.devices || []));
          setLoading(false);
          return;
        }
        
        // ✅ 단일 기기 계약: 해당 기기의 완료 여부만으로 상태 판단 (다기기 연속 계약 지원)
        if (fromQueryDeviceId0) {
          const targetDevice = (appData.devices || []).find((d: any) => d.id === fromQueryDeviceId0);
          console.log('🔍 [ContractPage] 대상 기기 확인:', { id: fromQueryDeviceId0, device: targetDevice });
          if (!targetDevice) {
            console.log('❌ [ContractPage] 대상 기기를 찾을 수 없음');
            setError('대상 기기를 찾을 수 없습니다.');
            setLoading(false);
            return;
          }

          // 🚫 웨어러블 기기는 계약서 불가
          if (isWearable(targetDevice.deviceType)) {
            console.log('⌚ [ContractPage] 웨어러블 기기는 계약서 불가');
            setError('웨어러블 기기는 계약서 기능을 사용할 수 없습니다. 기존 입금 프로세스를 이용해 주세요.');
            setLoading(false);
            return;
          }

          const completedByStatus = ['입금대기', '완료', '반송'].includes((targetDevice as any).status);
          const completedByFlag = !!(targetDevice as any).contract_signed_at;
          console.log('🔍 [ContractPage] 완료 체크:', { status: (targetDevice as any).status, completedByStatus, completedByFlag });
          if (completedByStatus || completedByFlag) {
            console.log('✅ [ContractPage] 기기가 이미 완료됨, completed 상태로 설정');
            setContractStatus('completed');
          } else {
            console.log('📝 [ContractPage] 기기가 미완료, draft 상태로 설정');
            setContractStatus('draft');
          }
        } else {
          // 📦 디바이스 지정 없는 전체 계약: 기존 전역 플래그로 판단
          if ((appData as any).contract_signed_at) {
            setContractStatus('completed');
          }
        }
        
        setApplication(appData);
      } catch (err) {
        console.error('신청서 로드 실패:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadApplication();
  }, [application_id, location.search]);

  // ✅ 개별 조항 체크 핸들러
  const handleTermCheck = (termId: string, checked: boolean) => {
    const newCheckedState = {
      ...checkedTerms,
      [termId]: checked
    };
    setCheckedTerms(newCheckedState);
    
    // 모든 조항이 체크되었는지 확인
    const allChecked = CONTRACT_TERMS.every(term => newCheckedState[term.id]);
    if (allChecked !== allTermsChecked) {
      setAllTermsChecked(allChecked);
      if (allChecked) {
        setShowDetailedTerms(false); // 모든 조항 체크 시 접기
      }
    }
  };

  // ✅ 모두 체크 토글 핸들러
  const handleAllTermsToggle = (checked: boolean) => {
    setAllTermsChecked(checked);
    setShowDetailedTerms(!checked); // 토글 on이면 개별 조항 숨기기, off면 보이기
    
    const newCheckedState: Record<string, boolean> = {};
    CONTRACT_TERMS.forEach(term => {
      newCheckedState[term.id] = checked;
    });
    setCheckedTerms(newCheckedState);
  };

  // ✅ 필수 조항 체크 여부 확인
  const requiredTermsChecked = CONTRACT_TERMS
    .filter(term => term.required)
    .every(term => checkedTerms[term.id]);

  // ✍️ 서명 완료 핸들러
  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setSignatureCompleted(true);
      setSignatureData(signatureRef.current.toDataURL());
    }
  };

  // 🔧 서명 패드 초기화 및 리사이즈 처리
  useEffect(() => {
    const resizeSignaturePad = () => {
      if (signatureRef.current) {
        const canvas = signatureRef.current.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        
        // DPR 적용
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        if (ctx) {
          ctx.scale(dpr, dpr);
        }
        
        // 캔버스 스타일 크기 설정
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';
        
        // SignaturePad 재초기화
        signatureRef.current.clear();
      }
    };

    // 초기 설정
    setTimeout(resizeSignaturePad, 100);

    // 윈도우 리사이즈 이벤트
    window.addEventListener('resize', resizeSignaturePad);
    return () => window.removeEventListener('resize', resizeSignaturePad);
  }, []);

  // 🗑️ 서명 지우기
  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignatureCompleted(false);
      setSignatureData(null);
      
      // 서명 패드 재초기화
      const canvas = signatureRef.current.getCanvas();
      const rect = canvas.getBoundingClientRect();
      const ctx = canvas.getContext('2d');
      
      // DPR 재적용
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      if (ctx) {
        ctx.scale(dpr, dpr);
      }
    }
  };

  // 📄 계약서 제출 가능 여부
  const canSubmit = requiredTermsChecked && signatureCompleted && !isSubmitting;

  // 🔁 백엔드 API를 통한 기기 상태 업데이트 (상태 계산 로직 포함)
  const updateDeviceStatusToAwaitingDeposit = async (appId: string, deviceId: string) => {
    console.log(`🔄 [ContractPage] 기기 상태 업데이트 시작: ${deviceId} → 입금대기 + 계약 플래그`);
    
    const response = await fetch(`https://us-central1-refurb-buy.cloudfunctions.net/api/api/applications/${appId}/devices/${deviceId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ 
        status: '입금대기',
        contract_signed_at: new Date().toISOString()
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ [ContractPage] 기기 상태 업데이트 실패:', response.status, errorData);
      throw new Error(errorData.error || `HTTP ${response.status}: 기기 상태 업데이트 실패`);
    }

    const result = await response.json();
    console.log('✅ [ContractPage] 기기 상태 업데이트 성공:', result);
    
    return result;
  };

  // 📤 계약서 제출 핸들러
  const handleSubmit = async () => {
    if (!canSubmit || !application || !signatureData) return;

    setIsSubmitting(true);
    setSnackbarMessage('계약 체결 중...');

    try {
      // Firestore에 계약 정보 저장
      const params = new URLSearchParams(location.search);
      const fromQueryDeviceId = params.get('device_id');
      const contractData: any = {
        contract_terms_agreed: checkedTerms,
        signature_data: signatureData,
        contract_status: 'completed',
        updated_at: serverTimestamp(),
      };
      // 전역 계약 시간은 디바이스 지정 없는 경우에만 기록
      if (!fromQueryDeviceId) {
        contractData.contract_signed_at = serverTimestamp();
      }

      await updateDoc(doc(db, 'applications', application.id), contractData);

      // 🔁 계약 체결 직후: 단건 기기 상태를 "입금대기"로 변경
      // 우선순위: 쿼리 파라미터 device_id → 단일 기기 → 스마트폰/태블릿이 1대만 명확한 경우
      let targetDeviceId: string | null = null;
      const params2 = new URLSearchParams(location.search);
      const fromQueryDeviceId2 = params2.get('device_id');
      const devicesList = application.devices || [];
      if (fromQueryDeviceId2 && devicesList.some(d => d.id === fromQueryDeviceId2)) {
        targetDeviceId = fromQueryDeviceId2;
      } else if (devicesList.length === 1) {
        targetDeviceId = devicesList[0].id;
      } else {
        const eligibleDevices = devicesList.filter((d) => isSmartphone(d.deviceType) || isTablet(d.deviceType));
        if (eligibleDevices.length === 1) {
          targetDeviceId = eligibleDevices[0].id;
        }
      }

      if (targetDeviceId) {
        try {
          // 백엔드 API를 통해 상태와 계약 플래그를 동시에 업데이트
          const result = await updateDeviceStatusToAwaitingDeposit(application.id, targetDeviceId);
          console.log('✅ [ContractPage] 백엔드 업데이트 결과:', result);
          
          // 최신 데이터로 동기화 (백엔드가 자동 계산한 상위 상태 반영)
          const refetch = await getDoc(doc(db, 'applications', application.id));
          if (refetch.exists()) {
            const freshData = { id: refetch.id, ...refetch.data() } as Application;
            console.log('🔄 [ContractPage] 최신 데이터로 동기화:', freshData.status, freshData.devices?.map(d => ({ id: d.id, status: (d as any).status, contract_signed_at: !!(d as any).contract_signed_at })));
            setApplication(freshData);
          }
        } catch (e) {
          console.error('❌ [ContractPage] 기기 상태 업데이트 실패:', e);
          setSnackbarMessage('상태 업데이트에 실패했습니다. 새로고침 후 다시 확인해 주세요.');
        }
      } else {
        console.warn('대상 기기를 특정할 수 없어 단건 상태 변경을 생략했습니다.');
      }

      // PDF 생성 및 다운로드 (백그라운드에서 처리) - 매각일자는 계약 체결 시점
      setTimeout(() => {
        console.log('📄 [ContractPage] 계약 체결 후 PDF 생성 시작');
        handleGenerateAndDownloadPDF();
      }, 1000);

      setContractStatus('completed');
      setShowSuccessDialog(true);
      setSnackbarMessage('계약 체결이 완료되었습니다!');

    } catch (err) {
      console.error('계약 체결 실패:', err);
      setError('계약 체결 중 오류가 발생했습니다. 다시 시도해 주세요.');
      setContractStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 📄 PDF 생성 및 다운로드 함수
  const handleGenerateAndDownloadPDF = async () => {
    console.log('📄 [ContractPage] PDF 다운로드 시작');
    console.log('📄 [ContractPage] application:', !!application);
    console.log('📄 [ContractPage] signatureData:', !!signatureData);
    
    if (!application) {
      console.log('❌ [ContractPage] 신청서 데이터 누락');
      setSnackbarMessage('신청서 데이터가 없습니다.');
      return;
    }

    // 서명이 없어도 PDF 생성 가능하도록 수정
    if (!signatureData) {
      console.log('⚠️ [ContractPage] 서명 데이터 없음, 서명 없이 PDF 생성');
    }

    try {
      console.log('📄 [ContractPage] PDF 생성 함수 호출');
      const params = new URLSearchParams(location.search);
      const fromQueryDeviceId = params.get('device_id') || undefined;
      const pdfBlob = await generateContractPDF({
        application,
        signatureData: signatureData || '', // 빈 문자열로 대체
        contractTermsAgreed: checkedTerms,
        contractDate: new Date(), // 입금받기 버튼을 누른 시점
        deviceId: fromQueryDeviceId,
      });

      console.log('📄 [ContractPage] PDF 생성 성공, 다운로드 시작');
      const filename = `매매계약서_${application.user_name}_${application.application_number}.pdf`;
      downloadPDF(pdfBlob, filename);
      
      setSnackbarMessage('계약서 PDF가 다운로드되었습니다.');
      console.log('📄 [ContractPage] 다운로드 완료');
    } catch (err) {
      console.error('❌ [ContractPage] PDF 생성 실패:', err);
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류';
      setSnackbarMessage(`PDF 생성 중 오류가 발생했습니다: ${errorMessage}`);
    }
  };

  // 🚪 페이지 나가기 확인
  const handleExit = () => {
    if (contractStatus === 'completed') {
      navigate('/');
      return;
    }
    
    if (signatureCompleted || Object.values(checkedTerms).some(Boolean)) {
      setShowExitDialog(true);
    } else {
      navigate('/');
    }
  };

  // 🔄 로딩 스켈레톤
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 2 }}>
        <Stack spacing={3} maxWidth="md" mx="auto">
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
        </Stack>
      </Box>
    );
  }

  // ❌ 에러 상태
  if (error || !application) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f8fafc',
        p: 2 
      }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <ErrorOutline color="error" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {error || '데이터를 불러올 수 없습니다'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ mt: 2 }}>
            홈으로 돌아가기
          </Button>
        </Paper>
      </Box>
    );
  }

  // ✅ 계약 완료 상태
  if (contractStatus === 'completed') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 2 }}>
        <Stack spacing={3} maxWidth="md" mx="auto" alignItems="center" justifyContent="center" minHeight="100vh">
          <CheckCircleOutline color="success" sx={{ fontSize: 80 }} />
          <Typography variant="h4" fontWeight={700} textAlign="center">
            계약 체결 완료
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            매매 계약이 성공적으로 체결되었습니다.<br />
            영업일 기준 2-3일 내 입금 처리됩니다.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button 
              variant="outlined" 
              startIcon={<Download />}
              onClick={() => {
                console.log('📄 [ContractPage] 완료 화면에서 PDF 다운로드 클릭');
                handleGenerateAndDownloadPDF();
              }}
            >
              계약서 다운로드
            </Button>
            <Button variant="contained" onClick={() => navigate('/')}>
              홈으로 가기
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* 🔝 상단 고정 요약 카드 */}
      <Paper 
        elevation={2}
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderRadius: 0,
          borderBottom: '1px solid #e1e8ed'
        }}
      >
        <Box sx={{ maxWidth: 'md', mx: 'auto', p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={handleExit} size="small">
              <ArrowBack />
            </IconButton>
            <Box flex={1}>
              <Typography variant="h6" fontWeight={700}>
                중고휴대기기 매매 계약서
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {application.user_name} · {application.phone_number}
              </Typography>
            </Box>
            {(() => {
              const params = new URLSearchParams(location.search);
              const fromQueryDeviceId = params.get('device_id');
              const devices = application.devices || [];
              const selectedDevice = fromQueryDeviceId ? devices.find(d => d.id === fromQueryDeviceId) : null;
              const headerAmount = selectedDevice
                ? Number((selectedDevice as any).final_price ?? (selectedDevice as any).price_offer ?? 0)
                : Number((application.total_final_price || application.total_price_offer || application.final_price || application.price_offer || 0));
              return (
                <Chip 
                  label={`총 ${headerAmount.toLocaleString()}원`}
              color="primary"
              variant="filled"
              sx={{ fontWeight: 700 }}
                />
              );
            })()}
          </Stack>
        </Box>
      </Paper>

      {/* 📋 메인 콘텐츠 */}
      <Box sx={{ maxWidth: 'md', mx: 'auto', p: 2, pb: 10 }}>
        <Stack spacing={3}>
          {/* 📝 계약서 안내 */}
          <Card elevation={1}>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                판매하실 휴대기기의 매입거래를 정식으로 인정하는 필수 첨부 서류 입니다.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                아래 내용을 정확히 필독하신 후 고객님께서 자필로 직접 작성해주세요.
              </Typography>
            </CardContent>
          </Card>

          {/* 📊 매입 내용 */}
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600} sx={{ mb: 3 }}>
                [매입 내용]
              </Typography>
              
              {/* 매입 정보 테이블 */}
              <Box sx={{ 
                border: '1px solid #e1e8ed', 
                borderRadius: 2, 
                overflow: 'hidden',
                mb: 3
              }}>
                {/* 첫 번째 행 */}
                <Stack direction="row">
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    bgcolor: '#f8fafc', 
                    borderRight: '1px solid #e1e8ed',
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2" fontWeight={600}>매각일자</Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    borderRight: '1px solid #e1e8ed',
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2">
                      {new Date().toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    bgcolor: '#f8fafc', 
                    borderRight: '1px solid #e1e8ed',
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2" fontWeight={600}>판매자명</Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    p: 2,
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2">{application.user_name}</Typography>
                  </Box>
                </Stack>

                {/* 두 번째 행 */}
                <Stack direction="row">
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    bgcolor: '#f8fafc', 
                    borderRight: '1px solid #e1e8ed',
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2" fontWeight={600}>모델명</Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    borderRight: '1px solid #e1e8ed',
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2">
                      {(() => {
                        const params = new URLSearchParams(location.search);
                        const fromQueryDeviceId = params.get('device_id');
                        const devices = application.devices || [];
                        const selectedDevice = fromQueryDeviceId ? devices.find(d => d.id === fromQueryDeviceId) : null;
                        if (selectedDevice) return selectedDevice.deviceModel || '기기정보없음';
                        return (application.devices?.length === 1 
                          ? application.devices[0].deviceModel
                          : `${application.devices?.[0]?.deviceModel} 외 ${(application.devices?.length || 1) - 1}건`
                        );
                      })()}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    bgcolor: '#f8fafc', 
                    borderRight: '1px solid #e1e8ed',
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2" fontWeight={600}>연락처</Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 1, 
                    p: 2,
                    borderBottom: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2">{application.phone_number}</Typography>
                  </Box>
                </Stack>

                {/* 세 번째 행 */}
                <Stack direction="row">
                  <Box sx={{ 
                    flex: 1, 
                    p: 2, 
                    bgcolor: '#f8fafc', 
                    borderRight: '1px solid #e1e8ed'
                  }}>
                    <Typography variant="body2" fontWeight={600}>판매금액</Typography>
                  </Box>
                  <Box sx={{ 
                    flex: 3, 
                    p: 2
                  }}>
                    <Typography variant="body1" fontWeight={700} color="primary.main">
                      {(() => {
                        const params = new URLSearchParams(location.search);
                        const fromQueryDeviceId = params.get('device_id');
                        const devices = application.devices || [];
                        const selectedDevice = fromQueryDeviceId ? devices.find(d => d.id === fromQueryDeviceId) : null;
                        const amount = selectedDevice
                          ? Number((selectedDevice as any).final_price ?? (selectedDevice as any).price_offer ?? 0)
                          : Number((application.total_final_price || application.total_price_offer || application.final_price || application.price_offer || 0));
                        return `${amount.toLocaleString()}원`;
                      })()}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* 판매자 계좌 정보 */}
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>은행명:</strong> {application.bank_name || ''}
                </Typography>
                <Typography variant="body2">
                  <strong>예금주:</strong> {application.account_holder || application.user_name}
                </Typography>
                <Typography variant="body2">
                  <strong>계좌번호:</strong> {application.bank_account || ''}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* ✅ 계약 조항 섹션 */}
          <Card elevation={1}>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    계약 전 꼭 확인해 주세요
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    모든 조항을 확인하고 동의해 주세요. 필수 항목은 반드시 체크해야 합니다.
                  </Typography>
                </Box>

                {/* 모두 체크 토글 */}
                <Box sx={{ 
                  p: 2, 
                  bgcolor: allTermsChecked ? '#f0f9ff' : '#f8fafc', 
                  borderRadius: 2,
                  border: `1px solid ${allTermsChecked ? '#0ea5e9' : '#e1e8ed'}`
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allTermsChecked}
                          onChange={(e) => handleAllTermsToggle(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="subtitle1" fontWeight={600}>
                          모든 내용에 동의 합니다
                        </Typography>
                      }
                    />
                    
                    {/* 펼치기/접기 버튼 */}
                    <IconButton
                      onClick={() => setShowDetailedTerms(!showDetailedTerms)}
                      size="small"
                      sx={{ 
                        color: 'text.secondary',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                      }}
                    >
                      {showDetailedTerms ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Stack>
                  
                  {/* 상태 안내 */}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {showDetailedTerms ? '개별 조항을 확인하세요' : '모든 조항에 동의하셨습니다'}
                  </Typography>
                </Box>

                {/* 개별 조항 체크리스트 - 접기/펼치기 애니메이션 */}
                <Collapse in={showDetailedTerms} timeout={300}>
                  <Stack spacing={2}>
                    {CONTRACT_TERMS.map((term) => (
                      <Box key={term.id} sx={{ 
                        p: 2, 
                        border: '1px solid #e1e8ed', 
                        borderRadius: 2,
                        bgcolor: checkedTerms[term.id] ? '#f0fdf4' : '#ffffff'
                      }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checkedTerms[term.id] || false}
                              onChange={(e) => handleTermCheck(term.id, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Stack spacing={0.5}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {term.title}
                                </Typography>
                                {term.required && (
                                  <Chip label="필수" size="small" color="error" variant="outlined" />
                                )}
                              </Stack>
                              <Typography variant="body2" color="text.secondary">
                                {term.content}
                              </Typography>
                            </Stack>
                          }
                          sx={{ alignItems: 'flex-start', m: 0 }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Collapse>
              </Stack>
            </CardContent>
          </Card>

          {/* ✍️ 서명 섹션 */}
          <Card elevation={1}>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    서명
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    크게, 천천히 서명해 주세요. 지우기/다시쓰기 가능해요.
                  </Typography>
                </Box>

                {/* 서명 패드 */}
                <Box sx={{ 
                  border: '2px dashed #cbd5e1',
                  borderRadius: 2,
                  bgcolor: '#ffffff',
                  position: 'relative',
                  overflow: 'hidden', // 오버플로우 방지
                  ...(signatureCompleted && {
                    border: '2px solid #10b981',
                    bgcolor: '#f0fdf4'
                  })
                }}>
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      width: isMobile ? 350 : 500,
                      height: 200,
                      className: 'signature-canvas',
                      style: {
                        width: '100%',
                        height: '200px',
                        touchAction: 'none',
                        display: 'block',
                        margin: 0,
                        padding: 0,
                        border: 'none',
                        outline: 'none',
                        cursor: 'crosshair',
                        // 포인터 이벤트 정확성을 위한 추가 스타일
                        pointerEvents: 'auto',
                        position: 'relative',
                        zIndex: 1
                      }
                    }}
                    onEnd={handleSignatureEnd}
                    backgroundColor="transparent"
                    dotSize={2}
                    minWidth={1}
                    maxWidth={3}
                    throttle={16}
                  />
                  
                  {!signatureCompleted && (
                    <Box sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      color: '#94a3b8'
                    }}>
                      <Typography variant="body2" textAlign="center">
                        여기에 서명해 주세요
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* 서명 액션 버튼 */}
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button
                    variant="outlined"
                    startIcon={<Clear />}
                    onClick={handleClearSignature}
                    disabled={!signatureCompleted}
                  >
                    서명 지우기
                  </Button>
                  {signatureCompleted && (
                    <Chip
                      icon={<CheckCircleOutline />}
                      label="서명 완료"
                      color="success"
                      variant="filled"
                    />
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {/* 🏢 구매자 정보 */}
          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                구매자
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body1" fontWeight={600}>
                  주식회사 다원트레이드
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  사업자번호: 750-81-00782
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* 🔽 하단 고정 액션바 */}
      <Paper 
        elevation={8}
        sx={{ 
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          borderRadius: 0,
          borderTop: '1px solid #e1e8ed'
        }}
      >
        <Box sx={{ maxWidth: 'md', mx: 'auto', p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box flex={1}>
              <Typography variant="body2" color="text.secondary">
                {!requiredTermsChecked && '필수 조항을 확인해 주세요'}
                {requiredTermsChecked && !signatureCompleted && '서명을 완료해 주세요'}
                {canSubmit && '계약 체결 준비 완료'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => setShowConfirmDialog(true)}
              disabled={!canSubmit}
              sx={{
                minWidth: 140,
                fontWeight: 700,
                ...(canSubmit && {
                  bgcolor: '#059669',
                  '&:hover': { bgcolor: '#047857' }
                })
              }}
            >
              {isSubmitting ? '처리 중...' : '입금신청'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* 🔔 확인 다이얼로그 */}
      <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)}>
        <DialogTitle>계약 체결 확인</DialogTitle>
        <DialogContent>
          <Typography>
            매매 계약을 체결하시겠습니까?<br />
            계약 체결 후에는 취소할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>취소</Button>
          <Button variant="contained" onClick={handleSubmit}>
            계약 체결
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🚪 나가기 확인 다이얼로그 */}
      <Dialog open={showExitDialog} onClose={() => setShowExitDialog(false)}>
        <DialogTitle>
          <Warning color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
          페이지를 나가시겠어요?
        </DialogTitle>
        <DialogContent>
          <Typography>
            작성 중인 내용이 사라집니다.<br />
            정말 나가시겠어요?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExitDialog(false)}>계속 작성</Button>
          <Button variant="contained" color="warning" onClick={() => navigate('/')}>
            나가기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 📢 스낵바 */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </Box>
  );
}
